import { callGet, callPost, } from "apis";
import { isProd } from "../constants";
import {
  ExecutorVerificationStatus,
  ExecutorVerificationStep,
  ExecutorVerificationStepDetails,
} from "models/ExecutorVerification";
import { objectToCamel, objectToSnake } from "ts-case-convert"

export class KnowYourExecutorAPI {
  protected static baseURL = isProd
    ? "https://know-your-executor.weareatticus.com"
    : "https://know-your-executor-ppd1.weareatticus.com";

  protected static executorVerificationStatusCache:
    | ExecutorVerificationStatus[]
    | null = null;

  public static async fetchExecutorVerificationStatuses(
    userId:number,
    allowCache = true
  ): Promise<ExecutorVerificationStatus[]> {
    if (allowCache && KnowYourExecutorAPI.executorVerificationStatusCache) {
      return KnowYourExecutorAPI.executorVerificationStatusCache;
    }

    const statuses: ExecutorVerificationStatus[] = [];
    try {
      const fullPath = `${KnowYourExecutorAPI.baseURL}/users/${userId}/executor-verification-statuses`;
      const response = await callGet(fullPath);
      response?.data?.data?.forEach((status: ExecutorVerificationStatus) => {
        statuses.push(status);
      });

      KnowYourExecutorAPI.executorVerificationStatusCache = statuses;
    } catch (e: any) {
      console.error("couldn't fetch status list, " + e?.message);
    }

    return statuses;
  }

  public static async fetchExecutorVerificationSteps(userId:number): Promise<
    ExecutorVerificationStep[]
  > {
    const steps: ExecutorVerificationStep[] = [];
    try {
      const fullPath = `${KnowYourExecutorAPI.baseURL}/users/${userId}/executor-verification-steps`;
      const response = await callGet(fullPath);
      response?.data?.data?.forEach((step: ExecutorVerificationStep) => {
        steps.push(step);
      });
    } catch (e: any) {
      console.error("couldn't fetch steps, " + e?.message);
    }

    return steps;
  }

  public static async fetchExecutorVerificationStepDetails(userId:number): Promise<
    ExecutorVerificationStepDetails[]
  > {
    const stepDetailsArray: ExecutorVerificationStepDetails[] = [];
    try {
      const fullPath = `${KnowYourExecutorAPI.baseURL}/users/${userId}/executor-verification-step-details`;
      const response = await callGet(fullPath);
      response?.data?.data?.forEach(
        (stepDetails: ExecutorVerificationStepDetails) => {
          stepDetailsArray.push(stepDetails);
        }
      );
    } catch (e: any) {
      console.error("couldn't fetch step details, " + e?.message);
    }

    return stepDetailsArray;
  }

  public static async updateExecutorVerificationStep(userId:number, step: ExecutorVerificationStep): Promise<ExecutorVerificationStep | null> {
    try {
      const fullPath = `${KnowYourExecutorAPI.baseURL}/users/${userId}/executor-verification-steps`;
      const response = await callPost(fullPath, { ...objectToSnake(step) });
      if (!response || (response && response.status !== 200)) {
        throw new Error("error fetching response, got status " + response?.status)
      }

      return response?.data?.data ? objectToCamel(response.data.data) as unknown as ExecutorVerificationStep : null
    } catch (e: any) {
      console.error("couldn't set step status, " + e?.message);
    }

    return null
  }

  public static async submitAllForReview(userId:number): Promise<boolean> {
    try {
      const fullPath = `${KnowYourExecutorAPI.baseURL}/users/${userId}/executor-verification-ready`;
      const response = await callPost(fullPath);
      if (!response || (response && response.status !== 200)) {
        throw new Error("error fetching response, got status " + response?.status)
      }

      return true
    } catch (e: any) {
      console.error("couldn't submit all for review, " + e?.message);
    }

    return false
  }
}
